import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Settings as SettingsIcon, Bell, Shield, User, Save, Loader2, Globe } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useSettings, QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageAbout } from "@/components/page-about";
import { useTranslation } from "react-i18next";
import { supportedLanguages, type SupportedLanguage } from "@/i18n/config";

interface SystemSettings {
  timezone: string;
  serviceRegion: string;
  temperatureUnit: string;
  dateFormat: string;
}

export default function Settings() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('settings.title'), t('settings.about-description'));
  const { toast } = useToast();
  const [formData, setFormData] = useState<SystemSettings>({
    timezone: "America/Chicago",
    serviceRegion: "Minneapolis–St. Paul Metro",
    temperatureUnit: "Celsius",
    dateFormat: "MM/DD/YYYY",
  });

  const { data: settings, isLoading } = useSettings();

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: Partial<SystemSettings>) => {
      const response = await apiRequest("PATCH", "/api/settings", updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings });
      toast({
        title: t('settings.toast.updated-title'),
        description: t('settings.toast.updated-body'),
      });
    },
    onError: () => {
      toast({
        title: t('settings.toast.error-title'),
        description: t('settings.toast.error-body'),
        variant: "destructive",
      });
    },
  });

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate(formData);
  };

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code as SupportedLanguage);
    localStorage.setItem('healthwallet-language', code);
  };

  return (
    <div className="space-y-6">
      <PageAbout
        title={t('settings.title')}
        description={t('settings.about-description')}
        features={[
          t('settings.about-feature-profile'),
          t('settings.about-feature-notifications'),
          t('settings.about-feature-timezone'),
          t('settings.about-feature-temperature'),
          t('settings.about-feature-security'),
        ]}
        tips={[
          t('settings.about-tip-contact'),
          t('settings.about-tip-critical'),
          t('settings.about-tip-timezone'),
        ]}
      />

      <div>
        <h1 className="text-3xl font-semibold">{t('settings.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('settings.subheading')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card data-testid="card-profile-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('settings.section.profile')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('settings.field.full-name')}</Label>
              <Input id="name" placeholder={t('settings.field.full-name-placeholder')} data-testid="input-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('settings.field.email')}</Label>
              <Input id="email" type="email" placeholder="email@healthwallet.ai" data-testid="input-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('settings.field.phone')}</Label>
              <Input id="phone" type="tel" placeholder="(612) 555-0000" data-testid="input-phone" />
            </div>
            <Button className="w-full" data-testid="button-save-profile">
              {t('settings.cta.save-profile')}
            </Button>
          </CardContent>
        </Card>

        <Card data-testid="card-notification-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('settings.notifications')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label>{t('settings.notif.wallet')}</Label>
                <div className="text-sm text-muted-foreground">{t('settings.notif.wallet-help')}</div>
              </div>
              <Switch defaultChecked data-testid="switch-temp-alerts" />
            </div>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label>{t('settings.notif.contribution')}</Label>
                <div className="text-sm text-muted-foreground">{t('settings.notif.contribution-help')}</div>
              </div>
              <Switch defaultChecked data-testid="switch-delivery-updates" />
            </div>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label>{t('settings.notif.compliance')}</Label>
                <div className="text-sm text-muted-foreground">{t('settings.notif.compliance-help')}</div>
              </div>
              <Switch defaultChecked data-testid="switch-compliance-alerts" />
            </div>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label>{t('settings.notif.email')}</Label>
                <div className="text-sm text-muted-foreground">{t('settings.notif.email-help')}</div>
              </div>
              <Switch data-testid="switch-email-notifications" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-security-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('settings.section.security')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('settings.sec.2fa')}</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="flex-1" data-testid="button-enable-2fa">
                  {t('settings.sec.enable-2fa')}
                </Button>
              </div>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>{t('settings.sec.session-timeout')}</Label>
              <div className="text-sm text-muted-foreground">
                {t('settings.sec.session-timeout-help')}
              </div>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>{t('settings.sec.data-security')}</Label>
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-green-600 font-medium">{t('settings.sec.data-security-active')}</span>
              </div>
            </div>
            <Separator />
            <div className="space-y-2">
              <Link href="/audit">
                <Button variant="outline" className="w-full" data-testid="button-view-audit-logs">
                  {t('settings.sec.view-audit')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="card-language-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t('settings.language')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">{t('settings.languageDescription')}</p>
          <Select value={i18n.resolvedLanguage} onValueChange={handleLanguageChange}>
            <SelectTrigger data-testid="select-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {supportedLanguages.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.nativeName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card data-testid="card-system-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            {t('settings.section.system')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="timezone">{t('settings.field.timezone')}</Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(value) => setFormData({...formData, timezone: value})}
                  >
                    <SelectTrigger data-testid="select-timezone">
                      <SelectValue placeholder={t('settings.field.timezone-placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Chicago">America/Chicago (CST)</SelectItem>
                      <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
                      <SelectItem value="America/Denver">America/Denver (MST)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">{t('settings.field.region')}</Label>
                  <Select
                    value={formData.serviceRegion}
                    onValueChange={(value) => setFormData({...formData, serviceRegion: value})}
                  >
                    <SelectTrigger data-testid="select-region">
                      <SelectValue placeholder={t('settings.field.region-placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Minneapolis–St. Paul Metro">Minneapolis–St. Paul Metro</SelectItem>
                      <SelectItem value="Greater Twin Cities">Greater Twin Cities</SelectItem>
                      <SelectItem value="Minnesota Statewide">Minnesota Statewide</SelectItem>
                      <SelectItem value="Upper Midwest">Upper Midwest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temp-unit">{t('settings.field.temp-unit')}</Label>
                  <Select
                    value={formData.temperatureUnit}
                    onValueChange={(value) => setFormData({...formData, temperatureUnit: value})}
                  >
                    <SelectTrigger data-testid="select-temp-unit">
                      <SelectValue placeholder={t('settings.field.temp-unit-placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Celsius">Celsius (°C)</SelectItem>
                      <SelectItem value="Fahrenheit">Fahrenheit (°F)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date-format">{t('settings.field.date-format')}</Label>
                  <Select
                    value={formData.dateFormat}
                    onValueChange={(value) => setFormData({...formData, dateFormat: value})}
                  >
                    <SelectTrigger data-testid="select-date-format">
                      <SelectValue placeholder={t('settings.field.date-format-placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (ISO)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveSettings}
                  disabled={updateSettingsMutation.isPending}
                  data-testid="button-save-system-settings"
                >
                  {updateSettingsMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('settings.cta.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {t('settings.cta.save-config')}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
